import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { SortingToggleOptions } from '../../../types';
import { colors } from '../../../../../colors';
import PieChart from '../PieChart';
import { ExpandableModalChildProps } from '../../../../../components/ExpandableModal';
import BarChart from '../BarChart';
import { ComponentDimensions } from '../../../../../types/Layout';
import { useChartsContext } from '../context';
import useWidthBreakpoint from '../../../../../hooks/useWidthBreakpoint';

type Props = ExpandableModalChildProps & {
  activeToggleOption: SortingToggleOptions;
};

const Wrapper = forwardRef<HTMLDivElement, Props>((props, childRef) => {
  const { activeToggleOption } = props;
  const { handleModalOpen, handleModalClose, style, isModalOpen } = props;
  const { getChartData } = useChartsContext();
  const data = getChartData(activeToggleOption);

  const { isMatch } = useWidthBreakpoint();
  // Use a ref to access the DOM element
  const divRef = useRef<HTMLDivElement>(null);
  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);

  // Combine forwarded ref with local ref
  useEffect(() => {
    if (typeof childRef === 'function') {
      childRef(divRef.current);
    } else if (childRef) {
      childRef.current = divRef.current;
    }

    if (!divRef.current) {
      return;
    }

    setRootDimensions({
      height: divRef.current.clientHeight,
      width: divRef.current.clientWidth,
    });
  }, [childRef]);

  const handleWrapperClick = () => {
    if (!isMatch('md')) {
      return;
    }

    if (isModalOpen) {
      handleModalClose?.();
      return;
    }

    handleModalOpen?.();
  };

  return (
    <div
      style={{
        flex: 1,
        border: '1px solid rgba(50, 50, 50, 1)',
        borderRadius: 8,
        padding: 16,
        backgroundColor: 'rgba(30, 30, 30, 1)',
        boxShadow: '4px 0px 5px rgba(255, 255, 255, 0.2)',
        transition: 'opacity 0.5s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: 'pointer',
        opacity: 1,
        // opacity: isModalOpen ? 1 : 0,
        width: '100%',
        aspectRatio: 1,
        ...style,
      }}
      onClick={handleWrapperClick}
      ref={divRef}
    >
      <p
        style={{
          margin: 0,
          color: colors.white,
          fontWeight: 'bold',
          fontSize: 20,
          textAlign: 'center',
        }}
      >
        {activeToggleOption}
      </p>
      <div
        style={{
          height: '100%',
          display: 'flex',
          marginTop: -10,
          marginBottom: -30,
          alignItems: 'center',
        }}
      >
        {rootDimensions ? (
          <>
            <PieChart
              data={data}
              activeToggleOption={activeToggleOption}
              showAllData={isModalOpen}
              width={rootDimensions.width}
              height={rootDimensions.height}
            />
            {isModalOpen && (
              <BarChart
                data={data}
                activeToggleOption={activeToggleOption}
                showAllData={isModalOpen}
                width={rootDimensions.width}
                height={rootDimensions.height}
              />
            )}
          </>
        ) : null}
      </div>
    </div>
  );
});

Wrapper.displayName = 'Wrapper';

export default Wrapper;
